import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
} from "reactstrap";
// core components
import Header from "components/Headers/Header.js";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Pagination from "components/Pagination";
import { getEducationDataList } from "services/_educational_resources";
import { FormControlLabel, Switch } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { deleteEducationDataById } from "services/_educational_resources";
import { successToast } from "components/toast";
import { errorToast } from "components/toast";
import { updateEducationStatus } from "services/_educational_resources";
import { updateEducationalResourceSortOrders } from "services/_educational_resources";
import { getPermissions } from "context/AuthContext";

const Education = () => {
  const permissions = getPermissions();
  const navigate = useNavigate();
  const [educationData, setEducationData] = useState([]);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortOrder, setSortOrder] = useState({});

  const initialData = useCallback(async () => {
    let payload = `page=${page}&per_page=${perPage}`;

    const data = await getEducationDataList(payload);
    if (data.data) {
      setRecords(data.data.records || []);
      setEducationData(data.data.records);
      setTotalRecords(data.data.total_records || 0);
      if (data.data.total_records && perPage) {
        setTotalPages(Math.ceil(data.data.total_records / perPage));
      }
    }
  }, [page, perPage]);

  const handleSortOrderChange = (id, value) => {
    setSortOrder((prev) => ({
      ...prev,
      [id]: Number(value),
    }));
  };

  const handleSaveSortOrders = async () => {
    try {
      if (!Object.keys(sortOrder).length) {
        return errorToast("No sort orders changed");
      }

      // Prepare payload for API
      const payload = Object.entries(sortOrder).map(([id, sort_order]) => ({
        id,
        sort_order: Number(sort_order),
      }));

      if (payload.length === 0) {
        return errorToast("No sort orders to update");
      }

      // Call API to update sort orders in DB
      const response = await updateEducationalResourceSortOrders(payload);
      console.log("Update Sort Orders Response:", response);

      if (response?.isSuccess) {
        successToast(response.message);

        // Reset sortOrder input state
        setSortOrder({});

        // Refresh data from DB to reflect changes
        initialData();
      } else {
        errorToast(response.message || "Failed to update sort orders");
      }
    } catch (err) {
      console.error(err);
      errorToast("Failed to update sort orders");
    }
  };
  useEffect(() => {
    initialData();
  }, [initialData]);

  const prevButton = async () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const nextButton = async () => {
    if (totalPages > page) {
      setPage(page + 1);
    }
  };

  const handleEdit = async (id) => {
    navigate(`/admin/educational-resource/edit-educational-resource/${id}`);
  };

  const handleDelete = async (id) => {
    const data = await deleteEducationDataById({ id });
    if (data.data) {
      successToast(data.message);
      initialData();
    } else {
      errorToast(data.message);
    }
  };

  const createEducation = () => {
    navigate("/admin/educational-resource/create-educational-resource");
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      // Call your API to update the status
      const payload = { id, status: newStatus };
      const response = await updateEducationStatus(payload);

      if (response.data) {
        successToast(response.message);

        setEducationData((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, status: newStatus } : item
          )
        );
      } else {
        errorToast(response.message);
      }
    } catch (err) {
      console.error(err);
      errorToast("Something went wrong!");
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between">
                <h3>Educational Resource </h3>
                {permissions.includes("create-educational-resources") && (
                  <Button color="info" onClick={createEducation}>
                    Create
                  </Button>
                )}
              </CardHeader>
              <Table
                className="align-items-center table-flush"
                responsive
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead className="thead-light">
                  <tr>
                    <th scope="col" className="blog-th-small">
                      #
                    </th>
                    <th
                      scope="col"
                      style={{
                        width: "100px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                      }}
                    >
                      Sort Order
                    </th>
                    <th
                      scope="col"
                      className="blog-th-title"
                      style={{ width: "200px" }}
                    >
                      Title
                    </th>
                    <th
                      scope="col"
                      className="blog-th-short-desc"
                      style={{ width: "280px" }}
                    >
                      Short Description
                    </th>
                    <th
                      scope="col"
                      className="blog-th-meta-title"
                      style={{ width: "280px" }}
                    >
                      Meta Title
                    </th>
                    <th
                      scope="col"
                      className="blog-th-meta-desc"
                      style={{ width: "280px" }}
                    >
                      Meta Description
                    </th>
                    <th scope="col" className="blog-th-status">
                      Status
                    </th>
                    <th scope="col" className="blog-th-created-at">
                      Created At
                    </th>
                    {permissions.includes(
                      "update-educational-resources" ||
                        "delete-educational-resources"
                    ) && <th scope="col" style={{ width: "80px" }} />}
                  </tr>
                </thead>
                <tbody>
                  {educationData.length > 0 ? (
                    educationData.map((education, index) => (
                      <tr key={index}>
                        <th scope="row">{(page - 1) * perPage + index + 1}</th>
                        <td
                          style={{
                            width: "100px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                        >
                          <input
                            className="form-control-alternative"
                            type="text"
                            value={
                              sortOrder[education.id] !== undefined
                                ? sortOrder[education.id] // if admin has typed something
                                : education.sort_order || "" // otherwise show DB value
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d*$/.test(val)) {
                                // only allow digits
                                handleSortOrderChange(
                                  education.id,
                                  val ? Number(val) : ""
                                );
                              }
                            }}
                            style={{
                              width: "40px",
                              textAlign: "center",
                              padding: "5px",
                              borderRadius: "0.50rem",
                            }}
                          />
                        </td>
                        <td className="blog-td blog-th-title">
                          {education.title}
                        </td>
                        <td className="blog-td blog-th-short-desc">
                          {education.short_description}
                        </td>
                        <td className="blog-td blog-th-meta-title">
                          {education.meta_title}
                        </td>
                        <td className="blog-td blog-th-meta-desc">
                          {education.meta_description}
                        </td>
                        <td className="blog-th-status">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={education.status}
                                onChange={() =>
                                  handleStatusChange(
                                    education.id,
                                    !education.status
                                  )
                                }
                              />
                            }
                          />
                        </td>
                        <td className="blog-th-created-at">
                          {education.created_at
                            ? moment(education.created_at).format(
                                "DD MMMM, YYYY"
                              )
                            : "-"}
                        </td>
                        {permissions.includes(
                          "update-educational-resources" ||
                            "delete-educational-resources"
                        ) && (
                          <td className="text-center">
                            <UncontrolledDropdown>
                              <DropdownToggle
                                className="btn-icon-only text-light"
                                href="#pablo"
                                role="button"
                                size="sm"
                                color=""
                                onClick={(e) => e.preventDefault()}
                              >
                                <i className="fas fa-ellipsis-v" />
                              </DropdownToggle>
                              <DropdownMenu
                                className="dropdown-menu-arrow"
                                right
                              >
                                {permissions.includes(
                                  "update-educational-resources"
                                ) && (
                                  <DropdownItem
                                    onClick={() => handleEdit(education.id)}
                                  >
                                    <i
                                      className="fas fa-edit"
                                      style={{
                                        color: "#1976d2",
                                        cursor: "pointer",
                                        marginRight: "10px",
                                      }}
                                    ></i>
                                    Edit
                                  </DropdownItem>
                                )}
                                {permissions.includes(
                                  "delete-educational-resources"
                                ) && (
                                  <DropdownItem
                                    onClick={() => handleDelete(education.id)}
                                  >
                                    <i
                                      className="fas fa-trash"
                                      style={{
                                        color: "red",
                                        cursor: "pointer",
                                        marginRight: "10px",
                                      }}
                                    ></i>
                                    Delete
                                  </DropdownItem>
                                )}
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td>No record found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <Button color="info" onClick={handleSaveSortOrders}>
                  Update Sort
                </Button>
                <Pagination
                  page={page}
                  perPage={perPage}
                  totalRecords={totalRecords}
                  totalPages={totalPages}
                  records={records}
                  prevButton={prevButton}
                  nextButton={nextButton}
                />
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Education;
